/*
 * AI Assistant saved prompts: prompts a user reuses, stored on their account
 * (users/{uid}/assistantPrompts) or in localStorage when there is no
 * Firestore user (offline desktop). Prompts are saved and removed, never
 * edited: a changed prompt is saved as a new one.
 * Record: { id, text, mode: "ask" | "agent", createdAt }
 */
import online from "../configs/status.js";
import { v4 as uuidv4 } from "uuid";
import { toast } from "@zerodevx/svelte-toast";
import * as m from "@app/paraglide/messages.js";

/* Firebase */
import { auth } from "@app/configs/firebase.js";
import db from "@app/configs/firestore.js";
import { collection, doc, getDocs, setDoc, deleteDoc } from "firebase/firestore";

const LOCAL_STORAGE_KEY = "cc-assistant-saved-prompts";
export const SAVED_PROMPT_MAX_LENGTH = 4000;

// Newest first
export const savedPromptState = $state([]);

/*
 * Firestore user id, or null to use localStorage. Use the auth uid, not
 * authState.user.id (a team-user UUID for team members; see presetStore).
 */
function firestoreUid() {
    return online ? auth?.currentUser?.uid || null : null;
}

function promptsCollection(uid) {
    return collection(db, "users", uid, "assistantPrompts");
}

function readLocalPrompts() {
    try {
        const prompts = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY));
        return Array.isArray(prompts) ? prompts : [];
    } catch {
        return [];
    }
}

function writeLocalPrompts() {
    try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(savedPromptState));
    } catch {
        // Storage unavailable (private window): prompts last for this session only.
    }
}

function setPrompts(prompts) {
    const valid = prompts.filter((prompt) => prompt && typeof prompt.text === "string" && prompt.id);
    valid.sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
    savedPromptState.length = 0;
    valid.forEach((prompt) => savedPromptState.push(prompt));
}

export async function loadSavedPrompts() {
    const uid = firestoreUid();
    if (!uid) {
        setPrompts(readLocalPrompts());
        return;
    }
    try {
        const snapshot = await getDocs(promptsCollection(uid));
        setPrompts(snapshot.docs.map((promptDoc) => promptDoc.data()));
    } catch (error) {
        console.warn("[saved-prompts] load failed", error.code || error.message);
    }
}

function normalise(text) {
    return text.trim().replace(/\s+/g, " ");
}

export function isPromptSaved(text, mode) {
    const key = normalise(text);
    return savedPromptState.some((prompt) => prompt.mode === mode && normalise(prompt.text) === key);
}

/**
 * Save a prompt. Already-saved text (same mode) only shows a toast.
 * @param {{ text: string, mode: "ask" | "agent" }} params
 */
export async function savePrompt({ text, mode }) {
    const trimmed = text.trim().slice(0, SAVED_PROMPT_MAX_LENGTH);
    if (!trimmed) return;
    const promptMode = mode === "agent" ? "agent" : "ask";
    if (isPromptSaved(trimmed, promptMode)) {
        toast.push(m.assistant_saved_prompt_exists(), { classes: ["toast-info"] });
        return;
    }
    const prompt = { id: uuidv4(), text: trimmed, mode: promptMode, createdAt: new Date().toISOString() };
    savedPromptState.unshift(prompt);

    const uid = firestoreUid();
    if (!uid) {
        writeLocalPrompts();
        return;
    }
    try {
        await setDoc(doc(promptsCollection(uid), prompt.id), prompt);
    } catch (error) {
        console.warn("[saved-prompts] save failed", error.code || error.message);
        const index = savedPromptState.findIndex((item) => item.id === prompt.id);
        if (index !== -1) savedPromptState.splice(index, 1);
        toast.push(m.assistant_saved_prompt_save_failed(), { classes: ["toast-danger"] });
    }
}

/**
 * Remove a prompt. Resolves to the removed record (for Undo), or null.
 * @param {string} id
 */
export async function removeSavedPrompt(id) {
    const index = savedPromptState.findIndex((prompt) => prompt.id === id);
    if (index === -1) return null;
    const [removed] = savedPromptState.splice(index, 1);

    const uid = firestoreUid();
    if (!uid) {
        writeLocalPrompts();
        return removed;
    }
    try {
        await deleteDoc(doc(promptsCollection(uid), id));
        return removed;
    } catch (error) {
        console.warn("[saved-prompts] remove failed", error.code || error.message);
        savedPromptState.splice(Math.min(index, savedPromptState.length), 0, removed);
        toast.push(m.assistant_saved_prompt_remove_failed(), { classes: ["toast-danger"] });
        return null;
    }
}

/** Put back a prompt removed with removeSavedPrompt (Undo). */
export async function restoreSavedPrompt(prompt) {
    if (savedPromptState.some((item) => item.id === prompt.id)) return;
    setPrompts([...savedPromptState, prompt]);

    const uid = firestoreUid();
    if (!uid) {
        writeLocalPrompts();
        return;
    }
    try {
        await setDoc(doc(promptsCollection(uid), prompt.id), prompt);
    } catch (error) {
        console.warn("[saved-prompts] restore failed", error.code || error.message);
        const index = savedPromptState.findIndex((item) => item.id === prompt.id);
        if (index !== -1) savedPromptState.splice(index, 1);
        toast.push(m.assistant_saved_prompt_save_failed(), { classes: ["toast-danger"] });
    }
}
